import Client from "./client";
import {pageParams} from "./paging";

/**
 * Two questions, one endpoint (see GET /projects/:id/tickets):
 *
 *  - `sprintIndex` asks "what did this sprint contain", by the sprint's number
 *    within the project, and reports each ticket's swimlane as of that sprint.
 *    That's the Kanban board.
 *  - `currentSprintIds` asks "which tickets are in these sprints right now", by
 *    internal sprint id. That's the Backlog page's sprint columns, which group
 *    by a ticket's live sprint and have no swimlanes to snapshot.
 *
 * Neither is paged: both are bounded by the sprints asked for. The unbounded
 * ticket list is the Backlog column - see GetBacklog below.
 */
export const GetTickets = ({projectId, accessToken, sprintIndex, currentSprintIds}) => {
    return new Promise((resolve, reject) => {
        const params = {};
        if (sprintIndex !== undefined) params.sprintIndex = sprintIndex;
        if (currentSprintIds !== undefined) params.currentSprintIds = currentSprintIds.join(",");

        Client.get(`/api/projects/${projectId}/tickets`, {
            params,
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.tickets);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

/**
 * One page of the Backlog column - the tickets nobody has scheduled into a
 * sprint yet, in the order they've been arranged in.
 *
 * `filters` go to the server rather than being applied to the page here, which
 * is the point: filtering a page in the browser shows the matching tickets of
 * page one instead of the first page of matching tickets, and silently loses
 * everything past the boundary. `cursor` is whatever the previous page's
 * `nextCursor` was - opaque, and only ever handed back unchanged.
 *
 * `total` is only reported for the first page of a given set of filters (the
 * server doesn't recount on every scroll), so a caller keeps the one it was
 * given while appending.
 */
export const GetBacklog = ({projectId, accessToken, filters, limit, cursor}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/backlog`, {
            params: pageParams({filters, limit, cursor}),
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve({
                items: res.data.tickets,
                nextCursor: res.data.nextCursor,
                hasMore: res.data.hasMore,
                total: res.data.total,
            });
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const GetTicket = ({ticketId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/tickets/${ticketId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticket);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// The same ticket, addressed by this project's own number (the "ABC-42" on
// its card) rather than its database id - what opening one from a URL uses
// now (see Linked/useOpenRecord.js), so the lookup is scoped to the project
// it's opened from rather than to wherever the id happens to actually live.
export const GetTicketByNumber = ({projectId, projectNumber, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/tickets/number/${projectNumber}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticket);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// `version`/`base` are how the API tells a save that changed something else
// touched in the meantime from one that's a real conflict - see PUT
// /tickets/:id. `base` only needs to carry the fields actually being sent
// (their value as this client originally loaded the ticket), not the whole
// record.
export const UpdateTicket = ({ticketId, name, body, points, ticketTemplateId, sprintId, scopeId, swimlaneId, priority, assignedToId, fieldValues, order, sprintOrder, bugId, open, closeReason, version, base, accessToken}) => {
    const data = {};
    if (name !== undefined) data.name = name;
    if (body !== undefined) data.body = body;
    if (points !== undefined) data.points = points;
    if (ticketTemplateId !== undefined) data.ticketTemplateId = ticketTemplateId;
    if (sprintId !== undefined) data.sprintId = sprintId;
    if (scopeId !== undefined) data.scopeId = scopeId;
    if (swimlaneId !== undefined) data.swimlaneId = swimlaneId;
    if (priority !== undefined) data.priority = priority;
    if (assignedToId !== undefined) data.assignedToId = assignedToId;
    if (fieldValues !== undefined) data.fieldValues = fieldValues;
    if (order !== undefined) data.order = order;
    if (sprintOrder !== undefined) data.sprintOrder = sprintOrder;
    // The bug this task is the work for - sent by that bug's own "Work on
    // this" when it attaches or detaches a task (see Bugs/BugWork.js), since
    // the link is a column here rather than on the bug. null detaches.
    if (bugId !== undefined) data.bugId = bugId;
    // See useTicketEditor's closeTicket - open: false always arrives with a
    // closeReason, in the same request (see PUT /tickets/:id).
    if (open !== undefined) data.open = open;
    if (closeReason !== undefined) data.closeReason = closeReason;
    if (version !== undefined) data.version = version;
    if (base !== undefined) data.base = base;

    return new Promise((resolve, reject) => {
        Client.put(`/api/tickets/${ticketId}`, data, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticket);
        }).catch((e) => {
            reject({
                ...(e.response?.data || { error: "Something went wrong. Please try again." }),
                status: e.response?.status,
            });
        })
    })
}

// `bugId` is only passed when the task is being promoted from a bug (see
// components/Bugs/PromoteBugPanel.js) - it attaches the two so the task can
// say where it came from and open it. Creation-only; PUT /tickets/:id
// doesn't accept it.
export const CreateTicket = ({projectId, name, body, points, ticketTemplateId, sprintId, scopeId, priority, assignedToId, fieldValues, bugId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/tickets`, {name, body, points, ticketTemplateId, sprintId, scopeId, priority, assignedToId, fieldValues, bugId}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticket);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const GetProjectMembers = ({projectId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/members`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.members);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// At least one of projectId/organizationId is required by the API - pass
// whichever one(s) apply to the current view.
export const GetTicketTemplate = ({projectId, organizationId, accessToken}) => {
    return new Promise((resolve, reject) => {
        const params = {};
        if (projectId !== undefined) params.projectId = projectId;
        if (organizationId !== undefined) params.organizationId = organizationId;

        Client.get('/api/ticket-templates', {
            params,
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticketTemplates);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CreateTicketTemplate = ({projectId, type, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/ticket-templates`, {type}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticketTemplate);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const RenameTicketTemplate = ({templateId, type, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.put(`/api/ticket-templates/${templateId}`, {type}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticketTemplate);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const DeleteTicketTemplate = ({templateId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/ticket-templates/${templateId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const CreateTicketField = ({templateId, label, type, placeholder, data, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/ticket-templates/${templateId}/fields`, {label, type, placeholder, data}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticketField);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const UpdateTicketField = ({fieldId, label, type, placeholder, data, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.put(`/api/ticket-fields/${fieldId}`, {label, type, placeholder, data}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.ticketField);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const DeleteTicketField = ({fieldId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.delete(`/api/ticket-fields/${fieldId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}
